import { OrderProductStatus } from '@skygroup/shared/constants/OrderProductConsts';
import { Product } from './interfaces';

/**
 * Order-get reports products a failed refresh-rates run left non-bookable.
 * The backend derives the list from `product_status` in the DB (every TS
 * adapter stamps the offending product before returning its error), so it
 * covers every provider — not just the hotel rooms that come back inside the
 * structured refresh-rates error payload.
 *
 * Entries carry the raw stored `name` only. Composing what the user reads
 * ("Hilton Tel Aviv — Deluxe Double Room") is deliberately a client concern:
 * a room's stored name has no hotel context, and display strings must come
 * from the translation files rather than the API.
 */
export interface UnavailableProductRef {
  id: number;
  parent_id?: number | null;
  name?: string | null;
  type?: string | null;
  status?: string | null;
  reason?: string | null;
}

export interface UnavailableProductDisplay {
  id: number;
  /** Parent names prepended, e.g. "Hilton Tel Aviv — Deluxe Double Room". */
  displayName: string;
  /** `not_available` (supplier no longer has it) vs anything else. */
  status: string | null;
  /** True only for `not_available` — everything else "could not be confirmed". */
  isNoLongerAvailable: boolean;
  reason: string | null;
}

const NAME_SEPARATOR = ' — ';

/**
 * Status by order-product id, for badging nodes during render. Both the
 * "is this node flagged" test and the wording of its badge must come from this
 * one map — reading the status off the rendered tree instead would let a node
 * be badged with one reason and described with another.
 */
export const getUnavailableProductStatuses = (
  unavailableProducts?: UnavailableProductRef[] | null
): Map<number, string | null> =>
  new Map(
    (unavailableProducts ?? [])
      .filter((product) => Number.isFinite(Number(product?.id)))
      .map((product) => [Number(product.id), product?.status ?? null])
  );

/**
 * Ancestor names for `id`, outermost first, walking the cart's nested
 * `subProducts`. Empty when the id is not in the tree — which is expected
 * when there is no cart to render and only the flat API list is available.
 */
const findAncestorNames = (
  products: Product[] | undefined | null,
  id: number,
  trail: string[] = []
): string[] | null => {
  for (const product of products ?? []) {
    if (Number(product?.id) === id) {
      return trail;
    }
    const found = findAncestorNames(
      product?.subProducts,
      id,
      product?.name ? [...trail, product.name] : trail
    );
    if (found) {
      return found;
    }
  }
  return null;
};

const findProductById = (
  products: Product[] | undefined | null,
  id: number
): Product | null => {
  for (const product of products ?? []) {
    if (Number(product?.id) === id) {
      return product;
    }
    const found = findProductById(product?.subProducts, id);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * Full display names for the flagged products, in API order.
 *
 * `products` is the rendered cart tree and may be missing entirely (the API
 * failed hard and only named the dead products); in that case the stored name
 * is used on its own rather than dropping the entry.
 */
export const buildUnavailableProductDisplays = (
  unavailableProducts?: UnavailableProductRef[] | null,
  products?: Product[] | null
): UnavailableProductDisplay[] =>
  (unavailableProducts ?? [])
    .filter((entry) => Number.isFinite(Number(entry?.id)))
    .map((entry) => {
      const id = Number(entry.id);
      const nodeName = findProductById(products, id)?.name;
      const ancestors = findAncestorNames(products, id) ?? [];
      const ownName = nodeName || entry.name || '';

      const segments = [...ancestors, ownName].filter(Boolean);
      //de-duplicate adjacent repeats: a whole-hotel product can carry the same
      //name as its only room, and "Hilton — Hilton" reads as a bug
      const deduped = segments.filter(
        (segment, index) => segment !== segments[index - 1]
      );

      return {
        id,
        displayName: deduped.join(NAME_SEPARATOR),
        status: entry.status ?? null,
        isNoLongerAvailable:
          entry.status === OrderProductStatus.NOT_AVAILABLE,
        reason: entry.reason ?? null,
      };
    });

/**
 * User-facing sentences for the flagged products, keeping "no longer
 * available" (the supplier is sold out) apart from "could not be confirmed"
 * (bad dates, unexpected provider error) — the backend maintains that
 * distinction, so we must not tell someone a room sold out when it did not.
 * Returns one sentence per kind present, so a mixed order reads correctly.
 */
export const buildUnavailableProductMessages = (
  displays: UnavailableProductDisplay[],
  t?: {
    productNoLongerAvailable?: string;
    productCouldNotBeConfirmed?: string;
  }
): string[] => {
  const namesFor = (soldOut: boolean) =>
    displays
      .filter((display) => display.isNoLongerAvailable === soldOut)
      .map((display) => display.displayName)
      .filter(Boolean);

  const messages: string[] = [];
  const goneNames = namesFor(true);
  const unconfirmedNames = namesFor(false);

  if (goneNames.length) {
    messages.push(
      (t?.productNoLongerAvailable ?? '{products} is no longer available.')
        .replace('{products}', goneNames.join(', '))
    );
  }
  if (unconfirmedNames.length) {
    messages.push(
      (t?.productCouldNotBeConfirmed ?? '{products} could not be confirmed.')
        .replace('{products}', unconfirmedNames.join(', '))
    );
  }
  return messages;
};
